/**
 * AusMed scroll pagination — prev/next arrows sync to scroll position (first / last).
 *
 * Use on any horizontal scroller: wrap in an element with `data-ausmed-scroll-pagination`.
 *
 * Required descendants:
 * - [data-ausmed-scroll-target] — overflow-x scroll container
 * - [data-ausmed-scroll-prev] — button (recommended) or control
 * - [data-ausmed-scroll-next] — button (recommended) or control
 *
 * Optional:
 * - [data-ausmed-scroll-controls] — wrapper hidden when the row fits (no overflow)
 * - On root: `data-ausmed-scroll-hide-controls-when-fit` — set when controls should hide if no scroll needed
 *
 * Page-based pagination (collection/search) is handled in Liquid via `snippets/pagination-controls.liquid`
 * (no prev link on page 1, no next on last page). Shared visuals: `.ausmed-c-pagination` in ausmed-components.css.
 */

const roots = document.querySelectorAll('[data-ausmed-scroll-pagination]');

function setBoundaryState(el, atBoundary) {
  if (!el) return;
  if (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button') {
    el.toggleAttribute('disabled', atBoundary);
  }
  el.setAttribute('aria-disabled', atBoundary ? 'true' : 'false');
  el.classList.toggle('pagination__link--disabled', atBoundary);
}

function scrollByChunk(scroller, direction) {
  const amount = Math.max(240, Math.round(scroller.clientWidth * 0.85));
  scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
}

function refresh(root) {
  const scroller = root.querySelector('[data-ausmed-scroll-target]');
  const prev = root.querySelector('[data-ausmed-scroll-prev]');
  const next = root.querySelector('[data-ausmed-scroll-next]');
  const controls = root.querySelector('[data-ausmed-scroll-controls]');
  const hideControlsWhenFit = root.hasAttribute('data-ausmed-scroll-hide-controls-when-fit');

  if (!scroller || !prev || !next) return;

  const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  const needsScroll = maxScrollLeft > 2;

  if (hideControlsWhenFit && controls) {
    controls.hidden = !needsScroll;
    controls.setAttribute('aria-hidden', needsScroll ? 'false' : 'true');
  }

  if (!needsScroll) {
    setBoundaryState(prev, true);
    setBoundaryState(next, true);
    return;
  }

  const atStart = scroller.scrollLeft <= 1;
  const atEnd = scroller.scrollLeft >= maxScrollLeft - 1;

  setBoundaryState(prev, atStart);
  setBoundaryState(next, atEnd);
}

function bind(root) {
  const scroller = root.querySelector('[data-ausmed-scroll-target]');
  const prev = root.querySelector('[data-ausmed-scroll-prev]');
  const next = root.querySelector('[data-ausmed-scroll-next]');
  if (!scroller || !prev || !next) return;

  prev.addEventListener('click', () => {
    if (prev.disabled || prev.getAttribute('aria-disabled') === 'true') return;
    scrollByChunk(scroller, -1);
  });
  next.addEventListener('click', () => {
    if (next.disabled || next.getAttribute('aria-disabled') === 'true') return;
    scrollByChunk(scroller, 1);
  });

  scroller.addEventListener('scroll', () => refresh(root), { passive: true });
  window.addEventListener('resize', () => refresh(root), { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => refresh(root));
    ro.observe(scroller);
  }

  refresh(root);
  requestAnimationFrame(() => refresh(root));
}

for (const root of roots) {
  bind(root);
}
